// Keeps supported Regios BUY_X -> ADD_TO_CART gifts in the same cart mutation
// as their qualifying products. Unknown or server-only rules are deliberately
// left to Regios so the theme never guesses at gift eligibility.
(function () {
  const contracts = [];
  const giftAvailability = new Map();
  const giftAvailabilityRequests = new Map();
  const variantProductIds = new Map();
  const productContexts = new Map();
  const productRequests = new Map();
  let lastConfigText = '';

  const SUPPORTED_PRODUCT_ELIGIBILITY_TYPES = new Set([
    'ALL_PRODUCTS',
    'SPECIFIC_PRODUCTS',
    'SPECIFIC_PRODUCT_VARIANTS',
    'SPECIFIC_COLLECTIONS',
    'VENDOR_MATCH',
    'ON_SALE_VARIANTS',
    'SPECIFIC_PRODUCT_TAGS',
    'VARIANT_TITLE',
    'PRODUCT_METAFIELD',
    'PRODUCT_VARIANT_METAFIELD',
    'LINE_ITEM_PROPERTY',
  ]);

  const SUPPORTED_CUSTOMER_ELIGIBILITY_TYPES = new Set([
    'ALL_CUSTOMERS',
    'LOGGED_IN_CUSTOMERS',
    'SPECIFIC_CUSTOMERS',
    'SPECIFIC_CUSTOMER_TAGS',
    'SPECIFIC_CUSTOMER_SEGMENTS',
    'LOCATION',
    'METAFIELD',
  ]);

  function asIds(value) {
    if (Array.isArray(value)) return value.flatMap(asIds);
    if (value && typeof value === 'object') {
      const directId = value.id || value.variantId || value.variant_id || value.productId || value.product_id;
      return directId ? asIds(directId) : Object.values(value).flatMap(asIds);
    }
    return String(value || '')
      .split(',')
      .map((id) => id.trim().match(/(\d+)$/)?.[1] || '')
      .filter(Boolean);
  }

  function asStrings(value) {
    if (Array.isArray(value)) return value.flatMap(asStrings);
    if (value == null) return [];
    return String(value)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function normalizeMetafields(value) {
    const metafields = new Map();
    (Array.isArray(value) ? value : []).filter(Boolean).forEach((metafield) => {
      const namespace = String(metafield?.namespace || '').trim();
      const key = String(metafield?.key || '').trim();
      if (namespace && key && metafield?.value != null) {
        metafields.set(`${namespace}.${key}`, String(metafield.value));
      }
    });
    return metafields;
  }

  function normalizeProductContext(rawProduct, selectedVariant) {
    const product = rawProduct?.product || rawProduct;
    const productId = String(
      product?.productId || product?.product_id || product?.id || selectedVariant?.product_id || ''
    );
    if (!/^\d+$/.test(productId)) return null;

    const variants = new Map();
    const rawVariants = Array.isArray(product?.variants) ? product.variants : [];
    [...rawVariants, selectedVariant].filter(Boolean).forEach((variant) => {
      const variantId = String(variant?.id || variant?.variantId || variant?.variant_id || '');
      if (!/^\d+$/.test(variantId)) return;
      variants.set(variantId, {
        id: variantId,
        title: String(variant?.title || variant?.public_title || ''),
        priceInCents: Number(variant?.priceInCents ?? variant?.price ?? 0),
        compareAtPriceInCents: Number(
          variant?.compareAtPriceInCents ?? variant?.compare_at_price ?? 0
        ),
        metafields: normalizeMetafields(variant?.metafields),
      });
    });

    const rawTags = Array.isArray(product?.tags) ? product.tags : asStrings(product?.tags);
    return {
      productId,
      handle: String(product?.handle || ''),
      vendor: String(product?.vendor || '').trim().toLowerCase(),
      tags: new Set(rawTags.map((tag) => tag.toLowerCase())),
      collectionIds: new Set(asIds(product?.collectionIds || product?.collection_ids)),
      metafields: normalizeMetafields(product?.metafields),
      variants,
    };
  }

  function registerProductContext(rawProduct, selectedVariant) {
    const nextContext = normalizeProductContext(rawProduct, selectedVariant);
    if (!nextContext) return null;

    const currentContext = productContexts.get(nextContext.productId);
    if (currentContext) {
      nextContext.tags = new Set([...currentContext.tags, ...nextContext.tags]);
      nextContext.collectionIds = new Set([
        ...currentContext.collectionIds,
        ...nextContext.collectionIds,
      ]);
      nextContext.metafields = new Map([
        ...currentContext.metafields,
        ...nextContext.metafields,
      ]);
      nextContext.variants = new Map([
        ...currentContext.variants,
        ...nextContext.variants,
      ]);
      nextContext.handle ||= currentContext.handle;
      nextContext.vendor ||= currentContext.vendor;
    }

    productContexts.set(nextContext.productId, nextContext);
    nextContext.variants.forEach((_, variantId) => {
      variantProductIds.set(variantId, nextContext.productId);
    });
    return nextContext;
  }

  function registerEmbeddedProductContexts() {
    if (typeof window === 'undefined') return;
    registerProductContext(window.RegiosDOPP?.doppV3Fallbacks?.product);
    registerProductContext(window.RegiosDOPP_ProductPage);
    (window.RegiosDOPP?.cart?.items || []).forEach((item) => {
      registerProductContext(item?.product, item?.variant);
    });
  }

  function parseProductEligibility(eligibility = {}) {
    const type = String(eligibility.type || '');
    if (!SUPPORTED_PRODUCT_ELIGIBILITY_TYPES.has(type)) return null;

    return {
      type,
      productIds: new Set([
        ...asIds(eligibility.asListOfIds),
        ...asIds(eligibility.selectedProducts),
        ...asIds(Object.keys(eligibility.compactFormat || {})),
      ]),
      variantIds: new Set([
        ...asIds(Object.values(eligibility.compactFormat || {})),
        ...asIds(eligibility.selectedProductVariants),
      ]),
      collectionIds: new Set([
        ...asIds(eligibility.asListOfIds),
        ...asIds(eligibility.selectedCollections),
      ]),
      vendors: new Set(asStrings(eligibility.vendors).map((vendor) => vendor.toLowerCase())),
      tags: new Set(asStrings(eligibility.tagsToCheck).map((tag) => tag.toLowerCase())),
      variantTitles: asStrings(eligibility.variantTitlesToCheck),
      condition: String(eligibility.condition || 'EQUALS'),
      propertyName: String(eligibility.propertyName || ''),
      namespace: String(eligibility.namespace || ''),
      key: String(eligibility.key || ''),
      value: String(eligibility.value ?? ''),
    };
  }

  function parsePromotion(discountId, serializedPromotion) {
    try {
      const promotion = typeof serializedPromotion === 'string'
        ? JSON.parse(serializedPromotion)
        : serializedPromotion;
      const entryPoint = promotion?.config?.customLogic?.entryPoint;
      const addToCartNode = entryPoint?.then;
      if (
        promotion?.discountClass !== 'AUTO_ADD_TO_CART'
        || entryPoint?.type !== 'BUY_X'
        || addToCartNode?.type !== 'ADD_TO_CART'
        || (entryPoint.basedOn && entryPoint.basedOn !== 'QUANTITY')
      ) {
        return null;
      }

      const giftVariants = Object.entries(addToCartNode.variants || {}).flatMap(([productId, value]) => (
        asIds(value).map((variantId) => ({
          variantId,
          quantity: Math.max(1, Number(addToCartNode.quantities?.[productId]) || 1),
        }))
      ));
      const rawRequirements = entryPoint.requirements || [];
      const requirements = rawRequirements.map((requirement) => {
        const eligibility = parseProductEligibility(requirement?.productEligibility);
        if (!eligibility) return null;
        return {
          ...eligibility,
          quantity: Math.max(1, Number(requirement.quantity) || 1),
        };
      });
      if (
        !giftVariants.length
        || !requirements.length
        || requirements.some((requirement) => !requirement)
      ) {
        return null;
      }

      const customerEligibility = promotion.config?.customerEligibility || { type: 'ALL_CUSTOMERS' };
      const customerEligibilityType = String(customerEligibility.type || 'ALL_CUSTOMERS');
      if (!SUPPORTED_CUSTOMER_ELIGIBILITY_TYPES.has(customerEligibilityType)) return null;

      const marketEligibility = promotion.config?.marketEligibility || null;
      if (marketEligibility && marketEligibility.type !== 'SPECIFIC_MARKETS') return null;

      const qualifierProductIds = new Set(requirements.flatMap((requirement) => (
        requirement.type === 'SPECIFIC_PRODUCTS' ? [...requirement.productIds] : []
      )));
      const qualifierVariantIds = new Set(requirements.flatMap((requirement) => (
        requirement.type === 'SPECIFIC_PRODUCT_VARIANTS' ? [...requirement.variantIds] : []
      )));

      return {
        discountId: String(discountId),
        nodeId: String(addToCartNode.id || 'add-to-cart-0'),
        enabled: promotion.config?.isEnabled !== false,
        promoStartsAt: promotion.startDate || null,
        promoEndsAt: promotion.endDate || null,
        customerEligibility,
        marketEligibility,
        maximumTimesToApply: entryPoint.maximumTimesToApply == null
          || entryPoint.maximumTimesToApply === ''
          || !Number.isFinite(Number(entryPoint.maximumTimesToApply))
          ? null
          : Math.max(0, Number(entryPoint.maximumTimesToApply)),
        giftVariants,
        giftVariantId: giftVariants[0].variantId,
        giftVariantIds: giftVariants.map((gift) => gift.variantId),
        requirements,
        qualifierProductIds,
        qualifierVariantIds,
      };
    } catch (error) {
      console.warn(`[Regios Gift] Ignoring invalid promotion ${discountId}.`, error);
      return null;
    }
  }

  function refreshContracts() {
    if (typeof document === 'undefined') return contracts;
    registerEmbeddedProductContexts();
    const configElement = document.getElementById('regios-dopp-config');
    const configText = configElement?.textContent?.trim() || '';
    if (!configText || configText === lastConfigText) return contracts;

    try {
      const configRoot = JSON.parse(configText);
      const nextContracts = Object.entries(configRoot.discountConfigs || {})
        .map(([discountId, promotion]) => parsePromotion(discountId, promotion))
        .filter(Boolean);

      contracts.splice(0, contracts.length, ...nextContracts);
      lastConfigText = configText;
    } catch (error) {
      console.warn('[Regios Gift] Unable to read the embedded promotion config.', error);
    }
    return contracts;
  }

  function compareText(actualValue, expectedValue, condition = 'EQUALS') {
    const actual = String(actualValue ?? '').toLowerCase();
    const expected = String(expectedValue ?? '').toLowerCase();
    switch (condition) {
      case 'NOT_EQUAL': return actual !== expected;
      case 'CONTAINS': return actual.includes(expected);
      case 'STARTS_WITH': return actual.startsWith(expected);
      case 'ENDS_WITH': return actual.endsWith(expected);
      case 'EQUALS':
      default: return actual === expected;
    }
  }

  function getCustomerContext() {
    const buyerIdentity = window.RegiosDOPP?.customer || null;
    const customer = buyerIdentity?.customer || (buyerIdentity?.id ? buyerIdentity : null);
    return {
      isAuthenticated: Boolean(buyerIdentity?.isAuthenticated || customer),
      customer,
    };
  }

  function isCustomerEligible(eligibility = { type: 'ALL_CUSTOMERS' }) {
    const type = String(eligibility.type || 'ALL_CUSTOMERS');
    const { isAuthenticated, customer } = getCustomerContext();
    if (type === 'ALL_CUSTOMERS') return true;
    if (type === 'LOGGED_IN_CUSTOMERS') return isAuthenticated;
    if (type === 'LOCATION') {
      const countries = new Set(asStrings(eligibility.countryCodes).map((code) => code.toUpperCase()));
      return !countries.size || countries.has(String(window.RegiosDOPP?.countryCode || '').toUpperCase());
    }
    if (!customer) return false;

    if (type === 'SPECIFIC_CUSTOMERS') {
      const customerIds = new Set([
        ...asIds(eligibility.asListOfIds),
        ...asIds(eligibility.selectedCustomers),
      ]);
      return customerIds.has(asIds(customer.id)[0] || '');
    }
    if (type === 'SPECIFIC_CUSTOMER_TAGS') {
      const expectedTags = new Set(
        asStrings(eligibility.tagsToCheck).map((tag) => tag.toLowerCase())
      );
      const actualTags = asStrings(customer.tags).map((tag) => tag.toLowerCase());
      (customer.hasTags || []).forEach((entry) => {
        if (entry?.hasTag && entry?.tag) actualTags.push(String(entry.tag).toLowerCase());
      });
      return actualTags.some((tag) => expectedTags.has(tag));
    }
    if (type === 'SPECIFIC_CUSTOMER_SEGMENTS') {
      const segmentIds = new Set(asIds(eligibility.asListOfIds));
      const memberships = [
        ...(customer.segmentMemberships || []).filter((entry) => entry?.isMember),
        ...asStrings(customer.segments).map((segmentId) => ({ segmentId, isMember: true })),
      ];
      return memberships.some((entry) => (
        segmentIds.has(asIds(entry.segmentId)[0] || '')
      ));
    }
    if (type === 'METAFIELD') {
      const expectedKey = `${eligibility.namespace || ''}.${eligibility.key || ''}`;
      const metafields = normalizeMetafields(customer.metafields);
      const actualValue = customer.checkMetafield?.value
        ?? metafields.get(expectedKey);
      return actualValue != null
        && compareText(actualValue, eligibility.value, eligibility.condition);
    }
    return false;
  }

  function isMarketEligible(eligibility) {
    if (!eligibility) return true;
    if (eligibility.type !== 'SPECIFIC_MARKETS') return false;
    return new Set(asIds(eligibility.ids)).has(asIds(window.RegiosDOPP?.marketId)[0] || '');
  }

  function isActive(contract) {
    if (!contract) return false;
    const now = Date.now();
    const startsAt = contract.promoStartsAt ? Date.parse(contract.promoStartsAt) : null;
    const endsAt = contract.promoEndsAt ? Date.parse(contract.promoEndsAt) : null;
    return contract.enabled !== false
      && (!startsAt || Number.isNaN(startsAt) || now >= startsAt)
      && (!endsAt || Number.isNaN(endsAt) || now < endsAt)
      && isCustomerEligible(contract.customerEligibility)
      && isMarketEligible(contract.marketEligibility);
  }

  function registerVariantProduct(variantId, productId) {
    const normalizedVariantId = String(variantId || '');
    const normalizedProductId = String(productId || '');
    if (!/^\d+$/.test(normalizedVariantId) || !/^\d+$/.test(normalizedProductId)) return;

    variantProductIds.set(normalizedVariantId, normalizedProductId);
    refreshContracts().forEach((contract) => {
      contract.requirements.forEach((requirement) => {
        if (
          requirement.type === 'SPECIFIC_PRODUCTS'
          && requirement.productIds.has(normalizedProductId)
        ) {
          requirement.variantIds.add(normalizedVariantId);
          contract.qualifierVariantIds.add(normalizedVariantId);
        }
      });
    });
  }

  function getProductContext(variantId, productId) {
    registerEmbeddedProductContexts();
    const normalizedVariantId = String(variantId || '');
    const normalizedProductId = String(
      productId || variantProductIds.get(normalizedVariantId) || ''
    );
    return productContexts.get(normalizedProductId) || null;
  }

  function getContractsForItem(variantId, productId, options = {}) {
    const { activeOnly = true } = options;
    refreshContracts();
    const normalizedVariantId = String(variantId || '');
    const normalizedProductId = String(productId || variantProductIds.get(normalizedVariantId) || '');
    if (normalizedVariantId && normalizedProductId) {
      registerVariantProduct(normalizedVariantId, normalizedProductId);
    }

    const item = {
      id: normalizedVariantId,
      product_id: normalizedProductId,
      properties: options.properties || {},
    };
    return contracts.filter((contract) => (
      (!activeOnly || isActive(contract))
      && contract.requirements.some((requirement) => itemMatchesRequirement(item, requirement))
    ));
  }

  function getContractForVariant(variantId, options = {}) {
    return getContractsForItem(variantId, options.productId, options)[0] || null;
  }

  function getContractForProduct(productId, options = {}) {
    return getContractsForItem(null, productId, options)[0] || null;
  }

  function getProductIdFromPage(variantId) {
    const normalizedVariantId = String(variantId);
    const cartRow = document.querySelector(
      `.cart-item[data-variant-id="${normalizedVariantId}"][data-product-id]`
    );
    if (cartRow?.dataset.productId) return cartRow.dataset.productId;

    const metaProduct = window.meta?.product || window.ShopifyAnalytics?.meta?.product;
    const metaVariantIds = asIds(metaProduct?.variants || metaProduct?.variantId || metaProduct?.variant_id);
    if (metaProduct?.id && (!metaVariantIds.length || metaVariantIds.includes(normalizedVariantId))) {
      return String(metaProduct.id);
    }
    return '';
  }

  function getHandleFromPage(variantId, suppliedHandle) {
    if (suppliedHandle) return String(suppliedHandle);
    const normalizedVariantId = String(variantId);
    const productForms = [...document.querySelectorAll('product-form[data-product-handle]')];
    const matchingForm = productForms.find((form) => (
      [...form.querySelectorAll('[name="id"]')].some((input) => String(input.value) === normalizedVariantId)
    ));
    if (matchingForm?.dataset.productHandle) return matchingForm.dataset.productHandle;

    const pathMatch = window.location.pathname.match(/^\/products\/([^/?#]+)/);
    return pathMatch?.[1] || '';
  }

  async function loadProductByHandle(handle) {
    const normalizedHandle = String(handle || '').trim();
    if (!normalizedHandle) return null;
    const requestKey = `handle:${normalizedHandle}`;
    if (productRequests.has(requestKey)) return productRequests.get(requestKey);

    const request = fetch(`/products/${encodeURIComponent(normalizedHandle)}.js`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => response.ok ? response.json() : null)
      .then((product) => {
        if (product?.id && Array.isArray(product.variants)) {
          registerProductContext(product);
        }
        return product;
      })
      .catch(() => null);

    productRequests.set(requestKey, request);
    return request;
  }

  async function resolveVariantProductId(variantId, context = {}) {
    const normalizedVariantId = String(variantId || '');
    if (!/^\d+$/.test(normalizedVariantId)) return '';
    if (context.productId) {
      registerVariantProduct(normalizedVariantId, context.productId);
      return String(context.productId);
    }
    if (variantProductIds.has(normalizedVariantId)) return variantProductIds.get(normalizedVariantId);

    const pageProductId = getProductIdFromPage(normalizedVariantId);
    if (pageProductId) {
      registerVariantProduct(normalizedVariantId, pageProductId);
      return pageProductId;
    }

    const handle = getHandleFromPage(normalizedVariantId, context.productHandle);
    const pageProduct = await loadProductByHandle(handle);
    if (pageProduct?.variants?.some((variant) => String(variant.id) === normalizedVariantId)) {
      return String(pageProduct.id);
    }

    const requestKey = `variant:${normalizedVariantId}`;
    if (!productRequests.has(requestKey)) {
      productRequests.set(requestKey, fetch(`/variants/${normalizedVariantId}.js`, {
        headers: { Accept: 'application/json' },
      })
        .then((response) => response.ok ? response.json() : null)
        .then((variant) => variant?.product_id || variant?.featured_image?.product_id || null)
        .catch(() => null));
    }
    const variantProductId = await productRequests.get(requestKey);
    if (variantProductId) registerVariantProduct(normalizedVariantId, variantProductId);
    return variantProductId ? String(variantProductId) : '';
  }

  async function loadGiftAvailability(giftVariantId) {
    const normalizedVariantId = String(giftVariantId);
    if (giftAvailability.has(normalizedVariantId)) return giftAvailability.get(normalizedVariantId);
    if (giftAvailabilityRequests.has(normalizedVariantId)) {
      return giftAvailabilityRequests.get(normalizedVariantId);
    }

    const request = (async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 1200);
      try {
        const response = await fetch(`/variants/${normalizedVariantId}.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Variant lookup failed (${response.status})`);
        const variant = await response.json();
        const available = variant.available === true;
        giftAvailability.set(normalizedVariantId, available);
        return available;
      } catch (_) {
        giftAvailability.set(normalizedVariantId, false);
        return false;
      } finally {
        clearTimeout(timeoutId);
        giftAvailabilityRequests.delete(normalizedVariantId);
      }
    })();

    giftAvailabilityRequests.set(normalizedVariantId, request);
    return request;
  }

  function areGiftsAvailable(contract) {
    return contract.giftVariantIds.every((variantId) => giftAvailability.get(variantId) === true);
  }

  function itemMatchesRequirement(item, requirement) {
    const variantId = String(item?.id || item?.variant_id || '');
    const productId = String(item?.productId || item?.product_id || variantProductIds.get(variantId) || '');
    const product = getProductContext(variantId, productId);
    const variant = product?.variants.get(variantId) || null;

    switch (requirement.type) {
      case 'ALL_PRODUCTS':
        return Boolean(variantId);
      case 'SPECIFIC_PRODUCTS':
        return requirement.productIds.has(productId);
      case 'SPECIFIC_PRODUCT_VARIANTS':
        return requirement.variantIds.has(variantId);
      case 'SPECIFIC_COLLECTIONS':
        return Boolean(product) && [...requirement.collectionIds].some((id) => (
          product.collectionIds.has(id)
        ));
      case 'VENDOR_MATCH':
        return Boolean(product?.vendor) && requirement.vendors.has(product.vendor);
      case 'ON_SALE_VARIANTS':
        return Boolean(
          variant
          && variant.compareAtPriceInCents > 0
          && variant.compareAtPriceInCents > variant.priceInCents
        );
      case 'SPECIFIC_PRODUCT_TAGS':
        return Boolean(product) && [...requirement.tags].some((tag) => product.tags.has(tag));
      case 'VARIANT_TITLE':
        return Boolean(variant?.title) && requirement.variantTitles.some((title) => (
          compareText(variant.title, title, requirement.condition)
        ));
      case 'PRODUCT_METAFIELD':
        return Boolean(product)
          && product.metafields.has(`${requirement.namespace}.${requirement.key}`)
          && compareText(
          product.metafields.get(`${requirement.namespace}.${requirement.key}`),
          requirement.value,
          requirement.condition
        );
      case 'PRODUCT_VARIANT_METAFIELD':
        return Boolean(variant)
          && variant.metafields.has(`${requirement.namespace}.${requirement.key}`)
          && compareText(
          variant.metafields.get(`${requirement.namespace}.${requirement.key}`),
          requirement.value,
          requirement.condition
        );
      case 'LINE_ITEM_PROPERTY':
        return Object.prototype.hasOwnProperty.call(item?.properties || {}, requirement.propertyName)
          && compareText(
          item?.properties?.[requirement.propertyName],
          requirement.value,
          requirement.condition
        );
      default:
        return false;
    }
  }

  function getGiftMultiplier(contract, items) {
    if (!Array.isArray(items) || !contract?.requirements?.length) return 0;
    const multiples = contract.requirements.map((requirement) => {
      const quantity = items.reduce((total, item) => {
        if (!itemMatchesRequirement(item, requirement)) return total;
        const itemQuantity = Number(item.quantity ?? 1);
        return total + (Number.isInteger(itemQuantity) && itemQuantity > 0 ? itemQuantity : 0);
      }, 0);
      return Math.floor(quantity / requirement.quantity);
    });
    const multiplier = multiples.length ? Math.min(...multiples) : 0;
    return contract.maximumTimesToApply == null
      ? multiplier
      : Math.min(multiplier, contract.maximumTimesToApply);
  }

  function isQualifierVariant(variantId) {
    return Boolean(getContractForVariant(variantId));
  }

  function shouldAtomicAddVariant(variantId, options = {}) {
    const matchingContracts = getContractsForItem(variantId, options.productId, options);
    return matchingContracts.some(areGiftsAvailable);
  }

  async function prepareVariant(variantId, context = {}) {
    refreshContracts();
    if (context.product) registerProductContext(context.product);

    let productId = String(context.productId || variantProductIds.get(String(variantId)) || '');
    let product = getProductContext(variantId, productId);
    const handle = getHandleFromPage(variantId, context.productHandle);
    if (!product && handle) {
      const loadedProduct = await loadProductByHandle(handle);
      if (loadedProduct?.id) productId = String(loadedProduct.id);
      product = getProductContext(variantId, productId);
    }
    if (!productId) productId = await resolveVariantProductId(variantId, context);

    let matchingContracts = getContractsForItem(variantId, productId, {
      properties: context.properties,
    });
    if (!matchingContracts.length) {
      const resolvedProductId = await resolveVariantProductId(variantId, context);
      matchingContracts = getContractsForItem(variantId, resolvedProductId, {
        properties: context.properties,
      });
    }
    if (!matchingContracts.length) return [];

    await Promise.allSettled(matchingContracts.flatMap((contract) => (
      contract.giftVariantIds.map(loadGiftAvailability)
    )));
    return matchingContracts;
  }

  async function prepareItems(items, context = {}) {
    if (!Array.isArray(items)) return items;
    await Promise.allSettled(items.map((item) => prepareVariant(
      item?.id || item?.variant_id,
      {
        productId: item?.productId || item?.product_id || context.productId,
        productHandle: item?.productHandle || item?.handle || context.productHandle,
        product: item?.product || context.product,
        properties: item?.properties || context.properties,
      }
    )));
    return withGift(items);
  }

  function withGift(items) {
    if (!Array.isArray(items)) return items;
    refreshContracts();

    return contracts.reduce((nextItems, contract) => {
      if (!isActive(contract) || !areGiftsAvailable(contract)) return nextItems;
      const giftMultiplier = getGiftMultiplier(contract, items);
      if (!giftMultiplier) return nextItems;

      const giftsToAdd = contract.giftVariants.flatMap((gift) => {
        const alreadyIncluded = nextItems.some((item) => (
          String(item?.id || item?.variant_id) === gift.variantId
          && String(item?.properties?._regios_auto_added_by_discount_id) === contract.discountId
          && String(item?.properties?._regios_auto_add_to_cart_node_id) === contract.nodeId
        ));
        if (alreadyIncluded) return [];

        return [{
          id: Number(gift.variantId),
          quantity: giftMultiplier * gift.quantity,
          properties: {
            _regios_auto_added_by_discount_id: contract.discountId,
            _regios_auto_add_to_cart_node_id: contract.nodeId,
          },
        }];
      });

      return [...nextItems, ...giftsToAdd];
    }, items);
  }

  refreshContracts();

  window.EGRegiosBedGift = Object.freeze({
    contracts,
    refreshContracts,
    isActive,
    getContractsForItem,
    getContractForVariant,
    getContractForProduct,
    isQualifierVariant,
    shouldAtomicAddVariant,
    areGiftsAvailable,
    getGiftMultiplier,
    prepareItems,
    prepareVariant,
    registerVariantProduct,
    withGift,
  });
})();
